import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getSharedFormStyles } from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { getConditionalArray } from 'utils/data';

import { getMasterPermissionsToPage, selectIsBrokerCompany } from 'store/auth/selectors';
import { getLoanSpecificForms } from 'store/forms/actions';
import { getRolesAction, openDialogAction } from 'store/global/actions';
import {
  getApplicationIncomesAction,
  getLoanAssetsAction,
  getLoanBrokerConditionByApplicationIdAction,
  getLoanConditionByLoanIdAction,
  getLoanApplicationTasksAction,
} from 'store/loanApplications/actions';
import {
  selectGroupedByPriorToLoanConditionsList,
  selectIsLoanCanceled,
} from 'store/loanApplications/selectors';
import {
  getLoanRolesAction,
  getTaskCategoriesAction,
  getTaskTypeDynamicFieldAction,
  getTaskTypesAction,
} from 'store/system/actions';
import {
  getConditionsAction,
  getConditionSetsAction,
  getPriorToTypesAction,
  getSlaGroupsAction,
} from 'store/underwriting/actions';
import { getUsersAction } from 'store/users/actions';

import { UnderwritingTestIds } from '../../Underwriting.testIds';

import AddBrokerConditionForm from './AddBrokerConditionForm';
import AddConditionsForm from './AddConditionsForm';
import LoanCondition from './LoanCondition';

const Conditions = ({ setIsLoading }) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();

  const { applicationId } = useParams();

  const { isManageAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_CONDITIONS));

  const loanConditions = useSelector(selectGroupedByPriorToLoanConditionsList);
  const isLoanCanceled = useSelector(selectIsLoanCanceled);
  const isBrokerCompany = useSelector(selectIsBrokerCompany);

  const theme = useTheme();
  const classes = useMemo(() => getSharedFormStyles(theme), [theme]);

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    Promise.all([
      ...(isBrokerCompany
        ? [dispatch(getLoanBrokerConditionByApplicationIdAction({ applicationId }))]
        : [dispatch(getConditionsAction()), dispatch(getConditionSetsAction())]),
      ...getConditionalArray(!isBrokerCompany && loanId, () => [
        dispatch(getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true })),
      ]),
      ...getConditionalArray(loanId, () => [
        dispatch(getLoanApplicationTasksAction()),
        dispatch(getLoanSpecificForms({ loanId, applicationId })),
      ]),
      dispatch(getTaskCategoriesAction()),
      dispatch(getTaskTypesAction()),
      dispatch(getTaskTypeDynamicFieldAction()),
      dispatch(getApplicationIncomesAction({ applicationId })),
      dispatch(getLoanAssetsAction({ applicationId })),
      dispatch(getSlaGroupsAction()),
      dispatch(getUsersAction({ includeDeleted: true })),
      dispatch(getLoanRolesAction({ includeDeletedRoles: true })),
      dispatch(getRolesAction({ includeDeletedRoles: true })),
      dispatch(getPriorToTypesAction()),
    ])
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, loanId, setIsLoading, applicationId, isBrokerCompany]);

  const customOnAssignFormClick = useCallback(
    newForm => {
      dispatch(
        openDialogAction({
          header: 'Add conditions and condition sets',
          body: dialogProps => (
            <AddConditionsForm initialForm={newForm} loanId={loanId} {...dialogProps} />
          ),
          size: ModalSize.XL,
        }),
      );
    },
    [dispatch, loanId],
  );

  const customOnAssignBrokerFormClick = useCallback(
    newForm => {
      dispatch(
        openDialogAction({
          header: 'Add conditions and condition sets',
          body: dialogProps => (
            <AddBrokerConditionForm
              isBrokerCompany
              formName={FormNames.ADD_CONDITIONS_AND_CONDITIONS_SETS}
              initialForm={newForm}
              loanId={loanId}
              {...dialogProps}
            />
          ),
          size: ModalSize.XL,
        }),
      );
    },
    [dispatch, loanId],
  );

  const onAddConditions = useCallback(() => {
    dispatch(
      openDialogAction({
        header: isBrokerCompany ? 'Add condition' : 'Add conditions and condition sets',
        body: dialogProps => {
          return isBrokerCompany ? (
            <AddBrokerConditionForm
              isBrokerCompany
              customOnAssignFormClick={customOnAssignBrokerFormClick}
              formName={FormNames.ADD_CONDITION}
              {...dialogProps}
            />
          ) : (
            <AddConditionsForm
              customOnAssignFormClick={customOnAssignFormClick}
              loanId={loanId}
              {...dialogProps}
            />
          );
        },
        size: ModalSize.XL,
      }),
    );
  }, [customOnAssignBrokerFormClick, customOnAssignFormClick, dispatch, isBrokerCompany, loanId]);

  return (
    <div className="w-full flex flex-col ">
      {!loanConditions.length && (
        <Typography gutterBottom className="mb-0" component="div" variant="bold16">
          Conditions
        </Typography>
      )}
      {loanConditions.map(([priorTo, conditions]) => (
        <LoanCondition conditions={conditions} key={priorTo} priorTo={priorTo} />
      ))}

      {!isLoanCanceled && (
        <div className="mt-20">
          <Button
            data-testid={UnderwritingTestIds.LOAN_CONDITIONS_ADD_BUTTON}
            disabled={!isManageAllowed}
            sx={classes.addNewButton}
            type="button"
            variant="contained"
            onClick={onAddConditions}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

Conditions.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(Conditions);
